import axios from 'axios';

import type { ApiBaseResponse } from '@hotjar/webapp/domains/common/api';
import { keysToCamelCase, keysToSnakeCase } from '@hotjar/webapp/lib/convert-string';

import type { CreateOrderRequest } from './types/create-order';
import type { OrderStatusResponse, OrderStatusResponseDTO } from './types/order-status';
import type { OrderDefaultPaymentMethodRequest } from './types/default-payment-method';
import { OrderStatus } from './types/order-status';

export enum OrderAction {
  cancel = 'cancel_subscription_order',
  create = 'customer_and_subscription_creation_order',
  defaultPaymentMethod = 'set_default_payment_method_order',
}

interface OrderSucessResponse {
  identifier: string;
}

const BASE_ORDER_URL = '/api/checkout/orders';

const asyncTimeout = (interval: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, interval);
  });

// TODO: this endpoint should eventually communicate directly with magpie
// https://magpie-integration.live.eks.hotjar.com/external/docs#/Orders/submit_an_order_api_checkout_orders_post
// https://hotjar.atlassian.net/wiki/spaces/MP/pages/2718105636/Frontend+talking+directly+to+Magpie
export async function createNewOrder(
  accountId: number,
  request: CreateOrderRequest,
): Promise<ApiBaseResponse> {
  const url = `/api/v1/customers/${accountId}/order`;
  const response = await axios.post<ApiBaseResponse>(
    url,
    keysToSnakeCase<CreateOrderRequest>(request, true),
  );

  if (!response.data.success) {
    throw new Error();
  }

  return response.data;
}

export async function getOrderStatus(accountId: number): Promise<OrderStatusResponse> {
  const url = `/api/v1/customers/${accountId}/order`;

  try {
    const response = await axios.get<OrderStatusResponseDTO>(url);
    return keysToCamelCase(response.data);
  } catch (error) {
    // @ts-expect-error fix catch error type
    throw new Error(error);
  }
}

export async function cancelSubscription(accountId: number): Promise<OrderSucessResponse> {
  const { data } = await axios.post<OrderSucessResponse>(BASE_ORDER_URL, {
    [OrderAction.cancel]: {
      hotjar_account_id: accountId,
    },
  });
  return data;
}

export async function setDefaultPaymentMethod({
  accountId,
  paymentMethodId,
}: OrderDefaultPaymentMethodRequest): Promise<OrderSucessResponse> {
  const { data } = await axios.post<OrderSucessResponse>(BASE_ORDER_URL, {
    [OrderAction.defaultPaymentMethod]: {
      hotjar_account_id: accountId,
      payment_method_id: paymentMethodId,
    },
  });
  return data;
}

export async function pollGetOrderStatus(
  accountId: number,
  interval: number,
  emit: (status: string) => void,
): Promise<void> {
  const { status } = await getOrderStatus(accountId).catch(() => ({
    // if this errors it means there are no processing orders and we can continue
    status: OrderStatus.SUCCESSFUL,
  }));

  emit(status);

  if (status === OrderStatus.PROCESSING) {
    await asyncTimeout(interval);
    pollGetOrderStatus(accountId, interval, emit);
  }
}
